/*!

=========================================================
* Argon Dashboard React - v1.2.3
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
// reactstrap components
import {
  Card,
  CardHeader,
  Table,
  Container,
  Row,
  Button,
  Spinner,
  Toast,
} from 'reactstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { GlobalContext } from '../api/GlobalContext';
import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AudioRecorder, { canSwitchRecordingVisits } from '../api/Recorder.js';
import HashState from '../variables/hash';
import ConfirmationModal from '../components/Misc/ConfirmationModal.js';
import EditableLabel from '../components/Misc/EditableLabel';
import { EnableDisableButton } from '../components/Misc/ActionButtons';
import { useSortableData } from 'components/Misc/HeaderSorting';
import { useSelectAll } from '../hooks/useSelectAll';
// core components
import Header from 'components/Headers/Header';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ConversationRowName from '../components/Misc/ConversationRowName';
import { getSyncCalContext, runScheduleSync } from '../api/scheduleSync';
import VisitsSchedule from '../components/Visits/VisitsSchedule';
import SyncStatus from '../components/Visits/SyncStatus';
import { startOfDay } from '../components/Visits/visitsDay';
import ConversationDetailsCX from './ConversationDetailsCX';

// Comprehend from the list fills the EMR note, so only where the EMR is beside us.
const canComprehendFromList = (() => {
  if (window.location.protocol === 'chrome-extension:') return true;
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
})();

function getFullName(patient) {
  return patient.lastName
    ? `${patient.lastName}, ${patient.firstName}`
    : patient.firstName;
}

// Function to format the date into a readable format like "12th Dec"
function formatDate(dateString) {
  const date = new Date(dateString);
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return date.toLocaleDateString('en-GB', options);
}

function formatDateRangeCompact(start, end) {
  if (!start || !end) return 'Date Range';
  const fmt = (d) => `${d.getMonth() + 1}/${d.getDate()}`;
  return `${fmt(new Date(start))} – ${fmt(new Date(end))}`;
}

const LAST_SYNCED_KEY = 'syncCalLastSynced';

function formatSyncDate(isoDate) {
  if (!isoDate) return null;
  const [year, month, day] = String(isoDate).split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function formatSyncTime(iso, withDay = false) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  if (!withDay) return time;
  return `${date.toLocaleDateString('en-US', { weekday: 'short' })} ${time}`;
}

const SYNC_TONES = {
  warning: { background: '#FCF5EE', color: '#7B4B2A', note: '#9B8676' },
  success: { background: '#EDF7F0', color: '#1D6F42', note: '#5C8C72' },
  neutral: { background: '#F4F5F7', color: '#32325D', note: '#8898AA' },
};

const SYNC_KIND_TONES = {
  checking: 'neutral',
  adding: 'neutral',
  added: 'success',
  'up-to-date': 'neutral',
  partial: 'warning',
  'no-schedule': 'warning',
  ambiguous: 'warning',
  unreadable: 'warning',
  timeout: 'warning',
};

const COUNT_WORDS = [
  'Zero',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
];

function countWord(count) {
  return count < COUNT_WORDS.length ? COUNT_WORDS[count] : String(count);
}

// Minutes into `day` -> epoch ms (setHours handles DST).
const atMinute = (day, min) => new Date(day).setHours(0, min, 0, 0);

// Appointment timestamps travel as UTC without the suffix, the same as
// conversation created_at.
function formatAppointmentTime(scheduledAt) {
  return scheduledAt ? formatSyncTime(`${scheduledAt}Z`) : null;
}

// One row per appointment that did not become a visit: the ones the injector
// could not read, then the ones the backend turned down. Rows are labelled by
// appointment time, falling back to the patient name when we have no time.
function buildSyncFailures(missing, skipped, appointments) {
  const byName = new Map(
    (appointments || []).map((appointment) => [
      `${appointment.firstName} ${appointment.lastName}`.trim().toLowerCase(),
      appointment,
    ]),
  );

  const rows = (skipped || []).map((row) => ({
    label: formatAppointmentTime(row.scheduledAt) || 'Unscheduled',
    reason: row.reason || "couldn't read the patient name",
  }));

  (missing || []).forEach((name) => {
    const appointment = byName.get(String(name).trim().toLowerCase());
    rows.push({
      label: formatAppointmentTime(appointment?.scheduledAt) || name,
      reason: "we couldn't add this one",
      appointment,
    });
  });

  return rows;
}

// The injector throws page-specific messages; the panel shows one of three.
function classifySyncError(message) {
  const text = String(message || '');
  if (/open your schedule|no appointments found|no schedule response/i.test(text))
    return 'no-schedule';
  if (/which schedule is yours/i.test(text)) return 'ambiguous';
  return 'unreadable';
}

const Conversations = () => {
  const navigate = useNavigate();

  // Get our Globals
  const { apiClient } = useContext(GlobalContext);
  // User-related states
  const [syncCal, setSyncCal] = useState(false);
  const [syncCalContext, setSyncCalContext] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [lastSyncedAt, setLastSyncedAt] = useState(() =>
    localStorage.getItem(LAST_SYNCED_KEY),
  );
  const [userEMR, setUserEMR] = useState(null);
  const [isSyncingSchedule, setIsSyncingSchedule] = useState(false);

  // Delete confirmation modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(() => () => {});
  const [conversationName, setConversationName] = useState('');

  // Update the user patients and conversations the first time this component loads
  const [conversations, setConversations] = useState([]);
  const [uniqueGroups, setUniqueGroups] = useState([]); // List of unique groups
  const [filterGroup, _setFilterGroup] = useState(''); // Allow us to filer on the patient group
  const [refresh, setRefresh] = useState(false);
  const [transcriptionStatus, setTranscriptionStatus] = useState({});
  const [isRecording, setIsRecording] = useState(false);
  const [showDetails, setShowDetails] = useState(
    () => localStorage.getItem('showQuickRecord') === 'true',
  ); // State to toggle visibility
  const [recordingConversationId, setRecordingConversationId] = useState(null);
  const [calendarDay, setCalendarDay] = useState(() => startOfDay(new Date()));
  // Comprehend from the schedule: one visit at a time, run in a hidden details view.
  const [comprehending, setComprehending] = useState(null); // { id, skipPatientCheck }
  const [comprehendMismatch, setComprehendMismatch] = useState(null);
  const [startDate, setStartDate] = useState(() => {
    const stored = sessionStorage.getItem('startDate');
    return stored ? new Date(stored) : null;
  });

  const [endDate, setEndDate] = useState(() => {
    const stored = sessionStorage.getItem('endDate');
    return stored ? new Date(stored) : null;
  });
  const [showDatePicker, setShowDatePicker] = useState(false); // State to control the visibility of the Date Picker
  const [inputValue, setInputValue] = useState(''); // Input value for editable date range
  const datePickerRef = useRef(null);
  // State to track which conversations are selected
  const [selectedConversations, setSelectedConversations] = useState([]);

  // Function to handle clicks outside the Date Picker
  const handleClickOutside = (event) => {
    if (
      datePickerRef.current &&
      !datePickerRef.current.contains(event.target)
    ) {
      setShowDatePicker(false);
    }
  };

  useEffect(() => {
    // Attach event listener to the document
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup the event listener on unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Function to toggle the visibility of the Date Picker overlay
  const toggleDatePicker = (e) => {
    setShowDatePicker(!showDatePicker);
  };

  const platform = sessionStorage.getItem('platform');
  const canSwitchVisits = canSwitchRecordingVisits();

  function setFilterGroup(group) {
    _setFilterGroup(group);
    // Update the hash
    navigate(`/User/conversations#${group}`);
  }
  function refreshData() {
    setRefresh((prevRefresh) => !prevRefresh);
  }

  function saveDateRange(start, end) {
    if (start) {
      const startOfDay = new Date(start);
      startOfDay.setUTCHours(0, 0, 0, 0);
      sessionStorage.setItem('startDate', startOfDay);
    }

    if (end) {
      const endOfDay = new Date(end);
      endOfDay.setHours(23, 59, 59, 999);
      sessionStorage.setItem('endDate', endOfDay);
    }
  }

  const hash = HashState();
  // COM-491 schedule view; a patient's visit history keeps the table.
  const showSchedule =
    apiClient.features?.visits_schedule === true && !(filterGroup || hash);
  const syncSchedule = async ({ only } = {}) => {
    if (isSyncingSchedule) return;

    setIsSyncingSchedule(true);
    setSyncStatus({ kind: 'checking' });
    try {
      const result = await runScheduleSync(
        apiClient,
        syncCalContext || { emr: userEMR },
        {
          only,
          onSchedule: (schedule) =>
            setSyncStatus({
              kind: 'adding',
              count: schedule.appointments.length,
              dateLabel: formatSyncDate(schedule.date),
            }),
        },
      );

      const createdIds = result['New conversation IDs'] || [];
      const alreadyMade = (result['Conversations already made'] || []).length;
      const failures = buildSyncFailures(
        result['Patients without Schedule'] || [],
        result.skipped || [],
        result.appointments || [],
      );
      // Cancelled / Late Cancel / No Show: not added, shown by exact status.
      const cancelled = (result.cancelled || []).map(
        (row) =>
          `${formatAppointmentTime(row.scheduledAt) || 'Unscheduled'} · ${row.status}`,
      );
      const dateLabel = formatSyncDate(result.syncedDate);
      if (showSchedule && dateLabel) {
        const [y, m, d] = String(result.syncedDate).split('-').map(Number);
        changeCalendarDay(new Date(y, m - 1, d));
      }
      const syncedAt = new Date().toISOString();
      localStorage.setItem(LAST_SYNCED_KEY, syncedAt);
      setLastSyncedAt(syncedAt);

      if (failures.length) {
        setSyncStatus({
          kind: 'partial',
          added: createdIds.length + alreadyMade,
          total: createdIds.length + alreadyMade + failures.length,
          rows: failures,
          retry: failures.map((row) => row.appointment).filter(Boolean),
          cancelled,
        });
      } else if (!createdIds.length && alreadyMade) {
        setSyncStatus({
          kind: 'up-to-date',
          count: alreadyMade,
          dateLabel,
          syncedAt,
          cancelled,
        });
      } else {
        setSyncStatus({
          kind: 'added',
          count: createdIds.length,
          dateLabel,
          ids: createdIds,
          cancelled,
        });
      }

      refreshData();
      // A second pass after the GSI catches up — newly synced visits don't
      // always come back on the immediate refetch.
      setTimeout(refreshData, 1500);
    } catch (error) {
      if (apiClient.isTimeoutError(error)) {
        setSyncStatus({ kind: 'timeout' });
        refreshData();
        return;
      }
      setSyncStatus({
        kind: classifySyncError(error.message),
        message: error.message,
      });
      console.error('Error syncing schedule:', error);
    } finally {
      setIsSyncingSchedule(false);
    }
  };

  const undoSync = async (ids = []) => {
    let remove = ids;
    if (showSchedule) {
      // Undo never loses a recording: visits that already have audio stay.
      const recorded = new Set(
        conversations
          .filter((c) => c.secondsTranscribed > 0)
          .map((c) => c.conversationId),
      );
      remove = ids.filter((id) => !recorded.has(id));
      setSyncStatus({
        kind: 'undone',
        removed: remove.length,
        kept: ids.length - remove.length,
      });
    } else {
      setSyncStatus(null);
    }
    try {
      await Promise.all(remove.map((id) => apiClient.deleteConversation(id)));
    } catch (error) {
      toast.error('Could not undo the sync. Please try again.');
      console.error('Error undoing schedule sync:', error);
    }
    refreshData();
  };

  // Above the list: idle is a plain muted line, work in progress and results
  // are a soft panel with the action inline.
  const renderSyncBar = () => {
    if (!syncCal) return null;
    const status = syncStatus || {};
    const tone = SYNC_TONES[SYNC_KIND_TONES[status.kind]] || SYNC_TONES.neutral;

    const heading = (text) => (
      <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{text}</div>
    );
    const progressBar = (
      <div className="progress mt-2" style={{ height: 6 }}>
        <div
          className="progress-bar progress-bar-striped progress-bar-animated bg-success"
          style={{ width: '45%' }}
        />
      </div>
    );
    const linkButton = (label, onClick, muted = false) => (
      <span
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => e.key === 'Enter' && onClick()}
        style={{
          color: muted ? tone.note : tone.color,
          cursor: 'pointer',
          fontWeight: muted ? 400 : 600,
          marginRight: '1rem',
          textDecoration: 'underline',
        }}
      >
        {label}
      </span>
    );
    const actionButton = (label, onClick) => (
      <Button
        color="success"
        size="sm"
        className="mr-3"
        onClick={onClick}
        disabled={isSyncingSchedule}
      >
        {label}
      </Button>
    );
    const panel = (children) => (
      <div
        className="small"
        style={{
          background: tone.background,
          borderRadius: '0.375rem',
          color: tone.color,
          margin: '0.5rem 0.75rem',
          padding: '0.75rem',
        }}
      >
        {children}
      </div>
    );
    const note = (text) => <span style={{ color: tone.note }}>{text}</span>;
    const forDate = status.dateLabel ? ` for ${status.dateLabel}` : '';
    const cancelledNote = status.cancelled?.length ? (
      <div className="mt-1">
        {note(`Not added: ${status.cancelled.join(', ')}`)}
      </div>
    ) : null;

    switch (status.kind) {
      case 'checking':
        return panel(
          <>
            {heading('Checking your schedule...')}
            {progressBar}
          </>,
        );
      case 'adding':
        return panel(
          <>
            {heading(
              `Adding ${status.count} visit${status.count === 1 ? '' : 's'}${forDate}`,
            )}
            {progressBar}
          </>,
        );
      case 'added':
        return panel(
          <>
            {heading(
              `${status.count} visit${status.count === 1 ? '' : 's'} added${forDate}`,
            )}
            {cancelledNote}
            <div className="mt-2">
              {linkButton('Undo', () => undoSync(status.ids))}
              {linkButton('Dismiss', () => setSyncStatus(null), true)}
            </div>
          </>,
        );
      case 'up-to-date':
        return panel(
          <>
            {heading('Up to date')}
            <div className="mt-1" style={{ color: tone.note }}>
              {`All ${status.count} appointment${status.count === 1 ? '' : 's'}${forDate} are already in your list.`}
              {formatSyncTime(status.syncedAt)
                ? ` Last synced ${formatSyncTime(status.syncedAt)}.`
                : ''}
            </div>
            {cancelledNote}
          </>,
        );
      case 'partial':
        return panel(
          <>
            {heading(`${status.added} of ${status.total} visits added`)}
            {cancelledNote}
            <div className="mt-1">
              {`${countWord(status.rows.length)} appointment${status.rows.length === 1 ? '' : 's'} didn't come through:`}
            </div>
            {status.rows.map((row, index) => (
              <div
                key={`${row.label}-${index}`}
                className="mt-2"
                style={{
                  background: '#FFFFFF',
                  border: '1px solid rgba(123, 75, 42, 0.15)',
                  borderRadius: '0.375rem',
                  padding: '0.5rem 0.75rem',
                }}
              >
                {`${row.label} · ${row.reason}`}
              </div>
            ))}
            <div className="d-flex align-items-center mt-2">
              {status.retry.length > 0 &&
                actionButton(`Retry ${status.retry.length === 1 ? 'this one' : `these ${status.retry.length}`}`, () =>
                  syncSchedule({ only: status.retry }),
                )}
              {note(
                `The ${status.added} that worked ${status.added === 1 ? 'is' : 'are'} already in your list.`,
              )}
            </div>
          </>,
        );
      case 'no-schedule':
        return panel(
          <>
            <div>
              We couldn&apos;t find a schedule on that page. Open your schedule
              in your EMR, then try again.
            </div>
            <div className="d-flex align-items-center mt-2">
              {actionButton('Try again', () => syncSchedule())}
              {note('Nothing was added to your visit list.')}
            </div>
          </>,
        );
      case 'ambiguous':
        return panel(
          <>
            <div>{status.message}</div>
            <div className="d-flex align-items-center mt-2">
              {actionButton('Try again', () => syncSchedule())}
              {note('Nothing was added to your visit list.')}
            </div>
          </>,
        );
      case 'unreadable':
        return panel(
          <>
            {heading("We couldn't read your calendar")}
            <div className="mt-1">
              It loaded differently than we expected. Refresh your EMR and try
              again.
            </div>
            <div className="mt-2">{actionButton('Try again', () => syncSchedule())}</div>
          </>,
        );
      case 'timeout':
        return panel(
          <div>
            This is taking longer than expected. Refresh in about 60 seconds to
            see any visits that were created.
          </div>,
        );
      default:
        return (
          <div className="text-muted small px-3 py-1">
            Reads the schedule open in your EMR beside this panel
            {lastSyncedAt
              ? ` · last synced ${formatSyncTime(lastSyncedAt, true)}`
              : ''}
          </div>
        );
    }
  };

  useEffect(() => {
    getSyncCalContext(apiClient)
      .then((context) => {
        setUserEMR(context.emr);
        setSyncCalContext(context);
        setSyncCal(context.available); // Show or hide the "Sync Cal" button
      })
      .catch((error) => {
        console.error('Error fetching user details:', error);
      });
  }, [apiClient]);

  useEffect(() => {
    // Go get the conversations for this user
    if ((endDate && startDate) || (!endDate && !startDate)) {
      apiClient
        .updateConversations(
          hash,
          startDate?.toISOString(),
          endDate?.toISOString(),
        )
        .then((data) => {
          setConversations(apiClient.conversations);
          let transcriptionStatusMap = {};
          apiClient.conversations.forEach((convo) => {
            if (platform === 'ios') {
              transcriptionStatusMap[convo.conversationId] = 'Record';
            } else {
              const hasStartedRecording = convo.secondsTranscribed !== 0;
              transcriptionStatusMap[convo.conversationId] = hasStartedRecording
                ? 'Resume'
                : 'Record';
            }
          });
          setTranscriptionStatus(transcriptionStatusMap); // iterate over the list of conversations and map them to a dictionary of convoId:"Ready"
        })
        .catch((error) => {
          // probably no session key
          // console.log(error);
          console.log('Did not get conversations');
        });
    }
    apiClient
      .updatePatients()
      .then((data) => {
        setUniqueGroups(apiClient.patients);
      })
      .catch((error) => {
        // probably no session key
        // console.log(error);
        console.log('Did not get patients');
        console.log(error);
      });
  }, [apiClient, hash, refresh, startDate, endDate]);

  // Add function to check and reset session storage at midnight
  const checkAndResetDateStorage = () => {
    const lastResetDate = sessionStorage.getItem('lastResetDate');
    const today = new Date();
    const currentDate = today.toDateString();

    if (!lastResetDate || lastResetDate !== currentDate) {
      // Reset date range storage
      sessionStorage.removeItem('startDate');
      sessionStorage.removeItem('endDate');
      sessionStorage.setItem('lastResetDate', currentDate);
      return true;
    }
    return false;
  };

  useEffect(() => {
    // Check if we need to reset storage
    const wasReset = checkAndResetDateStorage();

    // Retrieve stored date range from session storage
    const storedStartDate = sessionStorage.getItem('startDate');
    const storedEndDate = sessionStorage.getItem('endDate');

    if (!wasReset && storedStartDate && storedEndDate) {
      // If stored values exist and weren't reset, use them
      setStartDate(new Date(storedStartDate));
      setEndDate(new Date(storedEndDate));
      setInputValue(
        `${formatDate(new Date(storedStartDate))} - ${formatDate(new Date(storedEndDate))}`,
      );
    } else {
      // Default to last 30 days
      const today = new Date();
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(today.getDate() - 30);
      const endOfToday = new Date(today);
      endOfToday.setHours(23, 59, 59, 999);

      setStartDate(thirtyDaysAgo);
      setEndDate(endOfToday);

      setInputValue(`${formatDate(thirtyDaysAgo)} - ${formatDate(today)}`);

      saveDateRange(thirtyDaysAgo.toISOString(), today.toISOString());
    }
  }, []);

  // Filter conversations by date range
  const filteredConversations = conversations.filter((conversation) => {
    const conversationDate = new Date(conversation.createdAt + 'Z');
    const twoHoursFromNow = new Date(Date.now() + 2 * 60 * 60 * 1000);
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);

    // Never show visits more than 2 hours in the future, except the rest of
    // today — a calendar sync creates the whole day in the morning.
    const horizon =
      endOfToday > twoHoursFromNow ? endOfToday : twoHoursFromNow;
    if (conversationDate > horizon) return false;

    // Adjust endDate to include the entire day by setting time to 23:59:59
    const adjustedEndDate = endDate
      ? new Date(endDate.setHours(23, 59, 59, 999))
      : null;

    const isAfterStartDate = startDate
      ? conversationDate >= new Date(startDate)
      : true;
    const isBeforeEndDate = adjustedEndDate
      ? conversationDate <= adjustedEndDate
      : true;

    const isSameDay =
      startDate && endDate && startDate.getTime() === endDate.getTime();

    return isSameDay
      ? conversationDate.toDateString() === startDate.toDateString()
      : isAfterStartDate && isBeforeEndDate;
  });

  // Apply sorting to filtered conversations using useSortableData
  const {
    sortedItems: sortedConversations,
    sortConfig,
    handleSort,
  } = useSortableData(filteredConversations, 'createdAt', 'desc');

  // Group sorted conversations by createdAt date
  const groupedConversations = sortedConversations.reduce(
    (groups, conversation) => {
      const date = formatDate(conversation.createdAt + 'Z');
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(conversation);
      return groups;
    },
    {},
  );

  //  If we have a hash, set the filter group to that
  useEffect(() => {
    // Loop over the uniqueGroups list. Each item in the list will have a uid element. Check if that hash matches any of the elements.uids
    if (hash && uniqueGroups.length > 0) {
      var foundPatient = false;
      for (var i = 0; i < uniqueGroups.length; i++) {
        var patient = uniqueGroups[i];
        if (patient.uid == hash) {
          foundPatient = true;
        }
      }
      if (foundPatient) {
        _setFilterGroup(hash);
      } else {
        console.log('Did not find patient');
        console.log(hash);
        console.log(uniqueGroups);
      }
    }
  }, [hash, uniqueGroups]);

  function deleteConversation(conversationId) {
    // Remove it from the list of conversations
    setConversations((prevConversations) =>
      prevConversations.filter(
        (conversation) => conversation.conversationId !== conversationId,
      ),
    );
    // API Call to delete the conversation
    apiClient
      .deleteConversation(conversationId)
      .then((data) => {
        console.log(data);
      })
      .catch((error) => {
        // probably no session key
        console.log(error);
        console.log('Did not delete conversation');
      });
  }

  function updateConversationDetails(conversationId) {
    apiClient
      .getConversationDetails(conversationId)
      .then((data) => {
        console.log(data);
        if ('error' in data) {
          console.log('Error getting conversation details');
          // Update the transcriptionStatus state with the error for the specific conversationId
          setTranscriptionStatus((prev) => ({
            ...prev,
            [conversationId]: data.error,
          }));
          return;
        }
        // Update the transcriptionStatus state with the fetched status or default to 'Record'
        setTranscriptionStatus((prev) => ({
          ...prev,
          [conversationId]: data.status || 'Record',
        }));
      })
      .catch((error) => {
        // If we get a string, then we can use it directly, otherwise we need to get the message from the error object
        const errorMsg =
          typeof error === 'string'
            ? error
            : (error && error.message) || String(error);
        // If error starts with "Expected:", log it but don't update the state
        if (errorMsg.startsWith('Expected:')) {
          console.log('Error getting conversation details');
          console.log(errorMsg);
          return;
        }
        // Log other errors and optionally update the transcription status
        console.log('Did not get conversation details');
        console.log(errorMsg);
        setTranscriptionStatus((prev) => ({
          ...prev,
          [conversationId]: error,
        }));
      });
  }

  function handleConversationSelection(event, conversationId) {
    if (event.target.checked) {
      // Add conversationId to the array
      setSelectedConversations([...selectedConversations, conversationId]);
      console.log(selectedConversations);
    } else {
      // Remove conversationId from the array
      setSelectedConversations(
        selectedConversations.filter((id) => id !== conversationId),
      );
      console.log(selectedConversations);
    }
  }

  // Use the custom hook for "Select All" functionality
  const allVisibleConversations = Object.values(groupedConversations).flat();
  const { selectAll: selectAllVisits, handleSelectAll: handleSelectAllVisits } =
    useSelectAll(
      allVisibleConversations,
      'conversationId',
      selectedConversations,
      setSelectedConversations,
    );

  function requestBulkDelete() {
    // Get the count of selected conversations
    const conversationCount = selectedConversations.length;
    setConversationName(`${conversationCount} conversation(s)`);

    setIsModalOpen(true);

    // This function is called when user confirms in the modal
    setConfirmAction(() => () => {
      // Loop over each selected ID and call your existing delete function
      selectedConversations.forEach((conversationId) => {
        deleteConversation(conversationId);
      });

      // Clear the selected list
      setSelectedConversations([]);

      // Close the modal
      setIsModalOpen(false);
    });
  }

  // Helper function to check if any uploads are in progress
  const isAnyUploadInProgress = () => {
    return Object.values(transcriptionStatus).some(
      (status) => status === 'Uploading Audio...',
    );
  };

  const handleRecordClick = (conversationId, convoName) => {
    if (recordingConversationId === conversationId) {
      // Stop recording for the current conversation
      setRecordingConversationId(null);
      setIsRecording(false);
      setConversationName('');

      if (platform === 'ios') {
        transcriptionStatus[recordingConversationId] = 'Record';
        setTranscriptionStatus({
          ...transcriptionStatus,
        });
      }
    } else if (recordingConversationId !== null && !canSwitchVisits) {
      toast.info(
        `Stop current recording - "${convoName}" before starting a new one`,
      );
    } else if (isAnyUploadInProgress() && !canSwitchVisits) {
      toast.info(
        'Please wait for the current upload to complete before starting a new recording.',
      );
    } else {
      // Start recording for the clicked conversation. The recorder stops
      // any active visit first and its audio keeps uploading.
      setRecordingConversationId(conversationId);
      setIsRecording(true);
      setConversationName(convoName);
    }
  };

  function changeCalendarDay(day) {
    const start = startOfDay(day);
    const end = new Date(start);
    end.setHours(23, 59, 59, 999);
    setCalendarDay(start);
    // Load the day if it sits outside the current date range.
    if (startDate && endDate && (start < startOfDay(startDate) || end > endDate)) {
      setStartDate(start);
      setEndDate(end);
      setInputValue(`${formatDate(start)} - ${formatDate(end)}`);
      saveDateRange(start, end);
    }
  }

  async function saveVisitTimes(conversationId, start, end) {
    const results = await Promise.all([
      apiClient.editConversation(conversationId, 'scheduledStart', start),
      apiClient.editConversation(conversationId, 'scheduledEnd', end),
    ]);
    const failed = results.find((r) => r && 'error' in r);
    if (failed) throw new Error(failed.error);
  }

  function rescheduleVisit(conversationId, startMin, endMin) {
    const start = atMinute(calendarDay, startMin);
    const end = atMinute(calendarDay, endMin);
    setConversations((prev) =>
      prev.map((c) =>
        c.conversationId === conversationId
          ? { ...c, scheduledStart: start, scheduledEnd: end }
          : c,
      ),
    );
    saveVisitTimes(conversationId, start, end).catch((error) => {
      console.error('Error moving visit:', error);
      toast.error('Could not move that visit. Please try again.');
      refreshData();
    });
  }

  async function createVisitAt({ patientId, name, startMin, durationMin }) {
    let created = false;
    let uid = null;
    try {
      const data = await apiClient.newConversation(patientId || '');
      if (!data?.uid) {
        toast.error(data?.error || 'Could not add the visit. Please try again.');
        return false;
      }
      uid = data.uid;
      const start = atMinute(calendarDay, startMin);
      await Promise.all([
        saveVisitTimes(data.uid, start, start + durationMin * 60 * 1000),
        name ? apiClient.editConversation(data.uid, 'name', name) : null,
      ]);
      created = true;
    } catch (error) {
      console.error('Error adding visit:', error);
      // Without its times it would show up, unnamed, at the moment it was created.
      if (uid) await apiClient.deleteConversation(uid).catch(() => {});
      toast.error('Could not add the visit. Please try again.');
    }
    refreshData();
    return created;
  }

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    // Try to parse manually typed dates
    const [startStr, endStr] = value.split(' - ').map((item) => item.trim());
    if (startStr && endStr) {
      const parsedStart = new Date(startStr);
      const parsedEnd = new Date(endStr);
      if (!isNaN(parsedStart.getTime()) && !isNaN(parsedEnd.getTime())) {
        setStartDate(parsedStart);
        setEndDate(parsedEnd);
        // **Store selected date range in sessionStorage**
        saveDateRange(parsedStart, parsedEnd);
      }
    }
  };

  // Clear the selected date range
  const clearDateRange = () => {
    setStartDate(null);
    setEndDate(null);
    setInputValue('');
    sessionStorage.removeItem('startDate');
    sessionStorage.removeItem('endDate');
    refreshData();
  };

  return (
    <>
      <Header isLite={true} flush={showSchedule} />
      {/* Page content */}
      <Container
        className={`mt--7${showSchedule ? ' cpt-visit-flush' : ''}`}
        fluid
        style={{ overflow: 'hidden', width: '100%' }}
      >
        {/* Table */}
        <Row>
          <div className="col">
            {showSchedule ? (
              <Card className="shadow">
                <VisitsSchedule
                  conversations={conversations}
                  day={calendarDay}
                  onDayChange={changeCalendarDay}
                  sync={{
                    available: syncCal,
                    busy: isSyncingSchedule,
                    onSync: syncSchedule,
                    stamp: !lastSyncedAt
                      ? 'Not synced yet'
                      : new Date(lastSyncedAt).toDateString() ===
                          new Date().toDateString()
                        ? `Synced today ${formatSyncTime(lastSyncedAt)}`
                        : `Synced ${formatSyncTime(lastSyncedAt, true)}`,
                  }}
                  status={
                    <>
                    {comprehendMismatch && (
                      <div className="vs-status vs-warn">
                        <p className="vs-head">
                          {`This visit is for ${comprehendMismatch.visitPatientName || 'another patient'}, but the chart open in your EMR is${comprehendMismatch.emrPatientName ? ` for ${comprehendMismatch.emrPatientName}` : 'n’t'}.`}
                        </p>
                        <div className="vs-acts">
                          <button
                            type="button"
                            className="vs-btn vs-sync"
                            onClick={() => {
                              setComprehending({
                                id: comprehendMismatch.id,
                                skipPatientCheck: true,
                              });
                              setComprehendMismatch(null);
                            }}
                          >
                            Comprehend anyway
                          </button>
                          <button
                            type="button"
                            className="vs-linkbtn vs-quiet"
                            onClick={() => setComprehendMismatch(null)}
                          >
                            Go back
                          </button>
                        </div>
                      </div>
                    )}
                    {syncCal && (
                      <SyncStatus
                        status={syncStatus}
                        busy={isSyncingSchedule}
                        onClose={() => setSyncStatus(null)}
                        onUndo={undoSync}
                        onRetry={(only) => syncSchedule({ only })}
                        onTryAgain={() => syncSchedule()}
                      />
                    )}
                    </>
                  }
                  patients={uniqueGroups}
                  onCreateVisit={createVisitAt}
                  onParseDay={(payload) => apiClient.parseSchedule(payload)}
                  onOpen={(id) =>
                    navigate(`/User/conversations/details#${id}`)
                  }
                  canComprehend={canComprehendFromList}
                  comprehendingId={comprehending?.id}
                  onComprehend={(id) => {
                    if (comprehending) return;
                    setComprehendMismatch(null);
                    setComprehending({ id, skipPatientCheck: false });
                  }}
                  onReschedule={rescheduleVisit}
                  onDelete={deleteConversation}
                  onRefresh={refreshData}
                />
                {comprehending && (
                  <div style={{ display: 'none' }} aria-hidden="true">
                    <ConversationDetailsCX
                      key={`${comprehending.id}-${comprehending.skipPatientCheck}`}
                      visitId={comprehending.id}
                      headless
                      skipPatientCheck={comprehending.skipPatientCheck}
                      onDone={({ mismatch }) => {
                        if (mismatch) {
                          setComprehendMismatch({ id: comprehending.id, ...mismatch });
                        }
                        setComprehending(null);
                        refreshData();
                      }}
                    />
                  </div>
                )}
              </Card>
            ) : (
            <Card className="shadow">
              <CardHeader className="border-0 header-flex">
                <div className="d-flex align-items-center justify-content-between flex-wrap w-100">
                  <h3 className="mb-0" style={{ paddingRight: '10px' }}>
                    Visits
                  </h3>
                  {apiClient.features?.show_multi_recorder && (
                    <Button
                      color="info"
                      size="sm"
                      className="mr-2"
                      onClick={() =>
                        navigate('/User/conversations/multi-recorder')
                      }
                    >
                      Multi Recorder
                    </Button>
                  )}
                  <div>
                    {/* Quick Record and Toggle */}
                    <div className="quick-record-toggle">
                      {syncCal && (
                        <Button
                          color="primary"
                          size="sm"
                          onClick={syncSchedule}
                          disabled={isSyncingSchedule}
                        >
                          {isSyncingSchedule && (
                            <Spinner size="sm" className="mr-2" />
                          )}
                          {isSyncingSchedule ? 'Syncing...' : 'Sync Cal'}
                        </Button>
                      )}
                      <span className="quick-record-text">
                        Quick <br />
                        Record
                      </span>

                      <EnableDisableButton
                        checked={showDetails}
                        onChange={() => {
                          const next = !showDetails;
                          localStorage.setItem('showQuickRecord', String(next));
                          setShowDetails(next);
                        }}
                      />
                    </div>
                  </div>
                  <div className="d-flex gap-4 mt-3 align-items-start">
                    {/* Dropdown */}
                    <select
                      value={filterGroup}
                      onChange={(e) => setFilterGroup(e.target.value)}
                      className="patient-select"
                    >
                      <option value="">All Patients</option>
                      {uniqueGroups.map((group) => (
                        <option key={group.uid} value={group.uid}>
                          {getFullName(group)}
                        </option>
                      ))}
                    </select>
                    {/* Date Range Picker Section */}
                    <div style={{ position: 'relative' }}>
                      <button
                        onClick={toggleDatePicker}
                        className="date-range-btn"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.25rem',
                          border: '1px solid #ccc',
                          background: '#fff',
                          color: '#050505',
                          fontSize: '0.875rem',
                          fontWeight: 400,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          marginBottom: '0.3rem',
                          boxShadow: showDatePicker
                            ? '0 0 0 0.2rem rgba(94,114,228,0.25)'
                            : 'none',
                          borderColor: showDatePicker ? '#5e72e4' : '#ccc',
                        }}
                      >
                        <i
                          className="fa fa-calendar"
                          style={{ color: '#aaa', fontSize: '0.75rem' }}
                        />
                        <span>
                          {formatDateRangeCompact(startDate, endDate)}
                        </span>
                        {startDate || endDate ? (
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              clearDateRange();
                            }}
                            style={{
                              marginLeft: '2px',
                              color: '#8898aa',
                              fontWeight: 700,
                              lineHeight: 1,
                            }}
                          >
                            ×
                          </span>
                        ) : (
                          <i
                            className="fa fa-chevron-down"
                            style={{ fontSize: '0.6rem', color: '#aaa' }}
                          />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              {renderSyncBar()}
              <div
                className="tableFixHead table-responsive"
                style={{ maxHeight: '1000px', overflowY: 'auto' }}
              >
                <Table className="align-items-center table-flush table">
                  <thead className="thead-light">
                    <tr>
                      {!showDetails && (
                        <th
                          scope="col"
                          style={{
                            paddingLeft: '30px', // Add left padding
                            cursor:
                              selectedConversations.length > 0
                                ? 'pointer'
                                : 'not-allowed',
                            color:
                              selectedConversations.length > 0
                                ? '#000'
                                : '#aaa',
                          }}
                          onClick={() => {
                            if (selectedConversations.length > 0) {
                              requestBulkDelete();
                            } else {
                              // Optional: show a toast or do nothing
                              toast.info(
                                'Please select at least one conversation to delete.',
                              );
                            }
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectAllVisits}
                              onChange={handleSelectAllVisits}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                width: '16px',
                                height: '16px',
                              }}
                            />
                            <i
                              className="fa fa-trash"
                              aria-hidden="true"
                              style={{ fontSize: '1.2rem' }}
                            />
                          </div>
                        </th>
                      )}
                      <th
                        scope="col"
                        onClick={() => handleSort('conversationName')}
                        style={{ cursor: 'pointer' }}
                      >
                        Name{' '}
                        {sortConfig.key === 'conversationName'
                          ? sortConfig.direction === 'asc'
                            ? '▲'
                            : '▼'
                          : ''}
                      </th>
                      {!showDetails && (
                        <th
                          scope="col"
                          onClick={() => handleSort('createdAt')}
                          style={{ cursor: 'pointer' }}
                        >
                          Created At{' '}
                          {sortConfig.key === 'createdAt'
                            ? sortConfig.direction === 'asc'
                              ? '▲'
                              : '▼'
                            : ''}
                        </th>
                      )}
                      {showDetails && <th scope="col">Record</th>}
                      {showDetails && <th scope="col">Status</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(groupedConversations).length > 0 ? (
                      Object.keys(groupedConversations).map((date) => (
                        <React.Fragment key={date}>
                          <tr>
                            <td
                              colSpan={5}
                              style={{
                                textAlign: 'center',
                                fontWeight: 'bold',
                                backgroundColor: '#f4f5f7',
                              }}
                            >
                              {date}
                            </td>
                          </tr>
                          {groupedConversations[date].map((conversation) => (
                            <tr
                              key={conversation.conversationId}
                              className="clickable-row"
                              onClick={() =>
                                navigate(
                                  `/User/conversations/details#${conversation.conversationId}`,
                                )
                              }
                            >
                              {!showDetails && (
                                <td>
                                  <div
                                    style={{
                                      display: 'inline-block',
                                      paddingLeft: '8px', // Add left padding
                                      padding: '5px', // Adds a 5px buffer area around the checkbox
                                    }}
                                    onClick={(e) => e.stopPropagation()} // Prevent row click
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedConversations.includes(
                                        conversation.conversationId,
                                      )}
                                      onClick={(e) => e.stopPropagation()} // Prevent row click
                                      onChange={(e) =>
                                        handleConversationSelection(
                                          e,
                                          conversation.conversationId,
                                        )
                                      }
                                      style={{
                                        width: '16px', // Increase checkbox width
                                        height: '16px', // Increase checkbox height
                                      }}
                                    />
                                  </div>
                                </td>
                              )}
                              {showDetails && (
                                <td
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRecordClick(
                                      conversation.conversationId,
                                      conversation.conversationName,
                                    );
                                  }}
                                >
                                  <AudioRecorder
                                    recordButtonId={`audio-recorder-button-${conversation.conversationId}`}
                                    conversationID={conversation.conversationId}
                                    isDisabled={
                                      !canSwitchVisits &&
                                      ((recordingConversationId !== null &&
                                        recordingConversationId !==
                                          conversation.conversationId) ||
                                        (isAnyUploadInProgress() &&
                                          transcriptionStatus[
                                            conversation.conversationId
                                          ] !== 'Uploading Audio...'))
                                    }
                                    isRecording={isRecording}
                                    refresh={() =>
                                      updateConversationDetails(
                                        conversation.conversationId,
                                      )
                                    }
                                    setStatus={(statusReturned) => {
                                      const previousStatus =
                                        transcriptionStatus[
                                          conversation.conversationId
                                        ];
                                      setTranscriptionStatus((prev) => ({
                                        ...prev,
                                        [conversation.conversationId]:
                                          platform === 'ios'
                                            ? 'Record'
                                            : statusReturned,
                                      }));
                                      if (
                                        previousStatus ===
                                          'Uploading Audio...' &&
                                        statusReturned !== 'Uploading Audio...'
                                      ) {
                                        setTimeout(() => {
                                          setTranscriptionStatus((prev) => ({
                                            ...prev,
                                          }));
                                        }, 100);
                                      }
                                    }}
                                    seconds_transcribed={
                                      conversation.secondsTranscribed
                                    }
                                    remoteStop={() => {
                                      setRecordingConversationId(null);
                                      setIsRecording(false);
                                    }}
                                    icon={true}
                                  />
                                </td>
                              )}
                              <ConversationRowName
                                conversation={conversation}
                              />
                              {!showDetails && (
                                <td>
                                  {(() => {
                                    const utcDate = new Date(
                                      conversation.createdAt + 'Z',
                                    ); // Create a Date object in UTC
                                    return utcDate.toLocaleString([], {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      hour12: true,
                                      timeZone:
                                        Intl.DateTimeFormat().resolvedOptions()
                                          .timeZone, // Automatically use the user's time zone
                                    });
                                  })()}
                                </td>
                              )}
                              {showDetails && (
                                <td className="status-column">
                                  {
                                    transcriptionStatus[
                                      conversation.conversationId
                                    ]
                                  }
                                </td>
                              )}
                            </tr>
                          ))}
                          {Object.keys(groupedConversations).length === 1 && (
                            <tr>
                              <td colSpan={5} style={{ height: '100px' }}></td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={5}
                          style={{
                            textAlign: 'center',
                            padding: '1rem',
                            paddingTop: '6rem', // Adds space above the message
                            paddingBottom: '6rem', // Adds space below the message
                          }}
                        >
                          No data found for the selected date range.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card>
            )}
          </div>
        </Row>
      </Container>
      {showDatePicker && (
        <>
          <div
            onClick={() => setShowDatePicker(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.4)',
              zIndex: 9998,
            }}
          />
          <div
            ref={datePickerRef}
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%,-50%)',
              zIndex: 9999,
            }}
          >
            <DatePicker
              selected={startDate}
              onChange={(dates) => {
                let [start, end] = dates;
                if (start && end) setShowDatePicker(false);
                setStartDate(start);
                setEndDate(end);
                setInputValue(
                  `${formatDate(start)} - ${end ? formatDate(end) : ''}`,
                );
                saveDateRange(start, end);
              }}
              startDate={startDate}
              endDate={endDate}
              selectsRange
              inline
              dateFormat="MM/dd/yyyy"
              maxDate={new Date()}
            />
          </div>
        </>
      )}
      <ToastContainer />
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={confirmAction}
      >
        <>
          Delete <strong>{conversationName}</strong>? This is permanent and
          cannot be undone.
        </>
        {/* {`Delete ${conversationName}? This is permanent and cannot be undone.`} */}
        {/* {`Are you sure you want to delete "${conversationName}" ? They will be deleted permanently and cannot be restored again.`} */}
      </ConfirmationModal>
    </>
  );
};

export default Conversations;
